import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { playSfx } from "@/lib/sound";
import { Compass, Zap, Shield, Wrench, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Bot } from "lucide-react";

export default function CoreQuest() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const sRef = useRef(null);

  const apply = (data) => { setS(data); sRef.current = data; };
  const load = useCallback(async () => { const { data } = await api.get("/corequest/state"); apply(data); }, []);
  useEffect(() => { load(); }, [load]);

  const move = useCallback(async (dir) => {
    if (busy || sRef.current?.combat) return;
    setBusy(true);
    try { const { data } = await api.post("/corequest/move", { dir }); apply(data); if (data.encounter) playSfx("lose"); }
    catch (e) { toast.error(e.response?.data?.detail || "Blocked."); } finally { setBusy(false); }
  }, [busy]);

  const act = async (action) => {
    if (busy) return; setBusy(true);
    try {
      const { data } = await api.post("/corequest/combat", { action }); apply(data);
      if (data.result === "won") { playSfx("win"); toast.success(`Victory!${data.drop ? ` Recovered ${data.drop}!` : ""}`); }
      else if (data.result === "lost") toast.error("Unit disabled — back to the hub.");
    } catch (e) { toast.error(e.response?.data?.detail || "error"); } finally { setBusy(false); }
  };

  const travel = async (theme) => { const { data } = await api.post("/corequest/travel", { theme }); apply(data); };

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (["arrowup", "w"].includes(k)) { e.preventDefault(); move("up"); }
      else if (["arrowdown", "s"].includes(k)) { e.preventDefault(); move("down"); }
      else if (["arrowleft", "a"].includes(k)) { e.preventDefault(); move("left"); }
      else if (["arrowright", "d"].includes(k)) { e.preventDefault(); move("right"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move]);

  if (!s) return null;
  const combat = s.combat;

  return (
    <div data-testid="corequest-page" className="max-w-3xl mx-auto">
      <span className="label-cap text-primary flex items-center gap-2"><Compass className="w-4 h-4" /> CoreQuest // Grid Expedition</span>
      <h1 className="font-head font-black text-3xl sm:text-4xl mt-2 mb-1" style={{ color: s.tint }}>{s.theme_name}</h1>
      <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
        <span className="label-cap">Lv.<b className="text-foreground text-base">{s.level}</b></span>
        <div className="flex items-center gap-2"><span className="label-cap">XP</span>
          <div className="w-40 h-2 rounded bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(100, (s.xp / s.xp_next) * 100)}%` }} /></div>
          <span className="mono text-xs">{s.xp}/{s.xp_next}</span></div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="corequest-themes">
        {s.themes.map((t) => (
          <button key={t.id} data-testid={`cq-theme-${t.id}`} onClick={() => travel(t.id)} disabled={!!combat}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${t.id === s.theme ? "text-background" : "text-muted-foreground hover:text-foreground"}`}
            style={t.id === s.theme ? { background: t.tint, borderColor: t.tint } : { borderColor: "hsl(var(--border))" }}>{t.name}</button>
        ))}
      </div>

      <div className="relative panel-glow p-3">
        <div className="grid gap-0.5 mx-auto" style={{ gridTemplateColumns: "repeat(11, 1fr)", maxWidth: 440 }} data-testid="corequest-grid">
          {s.map.flatMap((row, r) => row.map((cell, c) => {
            const here = r === s.r && c === s.c;
            return (
              <div key={`${r}-${c}`} className="aspect-square rounded-sm grid place-items-center"
                style={{ background: cell === 1 ? `${s.tint}44` : "rgba(255,255,255,0.03)", border: cell === 1 ? `1px solid ${s.tint}66` : "1px solid rgba(255,255,255,0.04)" }}>
                {here && <Bot className="w-3.5 h-3.5" style={{ color: s.tint }} data-testid="cq-player" />}
              </div>
            );
          }))}
        </div>

        {combat && (
          <div data-testid="corequest-combat" className="absolute inset-0 bg-black/85 backdrop-blur-sm rounded-[var(--radius)] p-5 flex flex-col">
            <div className="text-center mb-3">
              <div className="label-cap">Encounter</div>
              <div className="font-head font-black text-2xl" style={{ color: s.tint }}>{combat.name}</div>
            </div>
            <div className="space-y-2 mb-3">
              <div><div className="flex justify-between text-xs mb-0.5"><span>{combat.name}</span><span className="mono">{Math.max(0, combat.hp)}/{combat.max}</span></div>
                <div className="h-2 rounded bg-muted overflow-hidden"><div className="h-full bg-destructive" style={{ width: `${Math.max(0, combat.hp / combat.max * 100)}%` }} /></div></div>
              <div><div className="flex justify-between text-xs mb-0.5"><span>Your Unit</span><span className="mono">{Math.max(0, combat.php)}/{combat.pmax}</span></div>
                <div className="h-2 rounded bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.max(0, combat.php / combat.pmax * 100)}%` }} /></div></div>
            </div>
            <div className="flex-1 overflow-y-auto bg-black/40 rounded p-2 mb-3 text-xs space-y-1 font-mono">
              {(combat.log || []).map((l, i) => <div key={i} className="text-muted-foreground">{l}</div>)}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="cq-laser" onClick={() => act("laser")} disabled={busy} className="btn-primary justify-center text-xs sm:text-sm py-2.5"><Zap className="w-4 h-4" /> Laser</button>
              <button data-testid="cq-barrier" onClick={() => act("barrier")} disabled={busy} className="btn-ghost justify-center text-xs sm:text-sm py-2.5"><Shield className="w-4 h-4" /> Barrier</button>
              <button data-testid="cq-nanites" onClick={() => act("nanites")} disabled={busy} className="btn-ghost justify-center text-xs sm:text-sm py-2.5"><Wrench className="w-4 h-4" /> Nanites</button>
            </div>
          </div>
        )}
      </div>

      {!combat && (
        <div className="flex flex-col items-center gap-2 mt-4">
          <button data-testid="cq-up" onClick={() => move("up")} className="w-14 h-14 rounded-xl border border-primary text-primary grid place-items-center"><ArrowUp /></button>
          <div className="flex gap-2">
            <button data-testid="cq-left" onClick={() => move("left")} className="w-14 h-14 rounded-xl border border-primary text-primary grid place-items-center"><ArrowLeft /></button>
            <button data-testid="cq-down" onClick={() => move("down")} className="w-14 h-14 rounded-xl border border-primary text-primary grid place-items-center"><ArrowDown /></button>
            <button data-testid="cq-right" onClick={() => move("right")} className="w-14 h-14 rounded-xl border border-primary text-primary grid place-items-center"><ArrowRight /></button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Use arrow keys / WASD or the pad. Watch for rogue drones (5% per step).</p>
        </div>
      )}
    </div>
  );
}
